import { useCallback, useEffect, useState } from "react";
import type { GetToken } from "./accountApi";
import { friendlyMessage, getAiSettings, getAiUsage, updateAiSettings } from "./accountApi";
import type { AiUsage, UpdateAiSettingsRequest, WorkspaceAiSettings } from "../models/ai";

/** Current month as YYYY-MM (local). */
function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Orchestration for a workspace's AI settings + monthly usage. The view renders this state and calls
 * the handlers; membership-scoped reads work for any member, while a settings change is admin-gated on
 * the server (a non-admin gets a 403 surfaced as `error`).
 */
export function useWorkspaceAi(getToken: GetToken, workspaceId: string | null) {
  const [settings, setSettings] = useState<WorkspaceAiSettings | null>(null);
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [period, setPeriod] = useState<string>(currentPeriod());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const [s, u] = await Promise.all([
        getAiSettings(getToken, workspaceId),
        getAiUsage(getToken, workspaceId, period),
      ]);
      setSettings(s);
      setUsage(u);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken, workspaceId, period]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async (req: UpdateAiSettingsRequest) => {
    if (!workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      setSettings(await updateAiSettings(getToken, workspaceId, req));
    } catch (e) {
      setError(friendlyMessage(e));
      // reload to discard the optimistic toggle if the server rejected it (e.g. non-admin 403)
      void load();
    } finally {
      setSaving(false);
    }
  }, [getToken, workspaceId, load]);

  return {
    settings, usage, period, setPeriod, loading, saving, error,
    reload: load, save, clearError: () => setError(null),
  };
}

export type WorkspaceAiController = ReturnType<typeof useWorkspaceAi>;
